import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import VehicleCard from "@/components/VehicleCard";
import Breadcrumbs from "@/components/Breadcrumbs";
import { categoryById } from "@/data/categories";
import { useCategories } from "@/hooks/useCategories";
import { useSEO } from "@/hooks/useSEO";
import { API } from "@/lib/api";

export default function CategoryPage() {
  const { id } = useParams();
  const meta = useCategories();
  const category = meta[id] || categoryById(id);
  const [vehicles, setVehicles] = useState(null);

  useSEO({
    title: category ? `${category.name} ${category.years} ქირაობა ტაქსისთვის — ${category.priceLabel}/დღე | taxipark.ge` : "taxipark.ge",
    description: category ? `${category.name} ${category.years} ქირით ტაქსზე თბილისში — ${category.priceLabel} დღიურად, დეპოზიტი ${category.depositLabel}. ${category.tagline}` : undefined,
    path: `/category/${id}`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (category) {
      axios.get(`${API}/vehicles`, { params: { category_id: id } }).then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
    }
  }, [id, category]);

  if (!category) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="category-not-found">
        <p className="text-gray-400 text-lg">კატეგორია ვერ მოიძებნა.</p>
        <Link to="/catalog" data-testid="category-not-found-back" className="btn-gold mt-8">კატალოგში დაბრუნება</Link>
      </main>
    );
  }

  const availableCount = (vehicles || []).filter((v) => v.status === "available").length;

  return (
    <main data-testid="category-page" className="pt-28 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <Breadcrumbs items={[
          { name: "მთავარი", path: "/" },
          { name: "კატალოგი", path: "/catalog" },
          { name: `${category.name} ${category.years}`, path: `/category/${id}` },
        ]} />
        <Link to="/catalog" data-testid="category-back-link" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> ყველა კატეგორია
        </Link>

        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#D4AF37]/12 text-[#E5C378] border border-[#D4AF37]/40">
          {category.badge}
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mt-4">
          {category.name} <span className="text-gray-400 font-medium">{category.years}</span>
        </h1>
        <p className="text-lg text-gray-400 mt-4 max-w-2xl leading-relaxed">{category.tagline}</p>

        {(() => {
          const guide = {
            prius: { path: "/prius-gakiraveba", label: "Prius ქირაობა ტაქსისთვის — სრული გზამკვლევი" },
            tesla: { path: "/tesla-gakiraveba", label: "Tesla ქირაობა ტაქსისთვის — სრული გზამკვლევი" },
            byd: { path: "/elektro-manqanebi-taxisistvis", label: "ელექტრო მანქანები ტაქსისთვის" },
          }[category.model];
          return guide ? (
            <Link to={guide.path} data-testid="category-guide-link" className="inline-flex items-center gap-2 mt-5 text-sm text-[#E5C378] hover:underline underline-offset-4">
              {guide.label} <ArrowRight className="w-4 h-4" />
            </Link>
          ) : null;
        })()}

        <div className="flex flex-wrap gap-6 mt-6 text-sm" data-testid="category-summary">
          <span className="text-gray-300">
            დღიური ფასი: <b className="text-white">{category.priceLabel}</b>
          </span>
          <span className="text-gray-300">
            დეპოზიტი: <b className="text-white">{category.depositLabel}</b>
          </span>
          {vehicles && (
            <span className="text-green-400" data-testid="category-available-count">
              {availableCount} თავისუფალი ავტომობილი
            </span>
          )}
        </div>

        <ul className="flex flex-wrap gap-x-6 gap-y-2 mt-6" data-testid="category-features">
          {category.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-gray-400">
              <Check className="w-4 h-4 text-[#D4AF37]" /> {f}
            </li>
          ))}
        </ul>

        <div className="border-t border-white/10 mt-10 pt-10">
          {vehicles === null ? (
            <p className="text-gray-500">იტვირთება...</p>
          ) : vehicles.length === 0 ? (
            <div className="card-lux p-12 text-center" data-testid="category-empty">
              <p className="text-gray-400">ამ კატეგორიაში ავტომობილები მალე დაემატება — დაგვირეკე და შეიტყვე პირდაპირ.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-vehicles-grid">
              {vehicles.map((v) => (
                <VehicleCard key={v.id} vehicle={v} category={category} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
